/**
 * MARCA | 19/05/2026 | Quick filter pills custom element.
 * Syncs togglable pill buttons with existing Search & Discovery filter checkboxes.
 * Clicking a pill toggles the matching checkbox in the facets form and triggers
 * the standard section re-render via the existing FacetsFormComponent.
 *
 * MARCA | 11/09/2026 | Every pill now carries a `data-filter-url` built by Shopify
 * (url_to_add / url_to_remove). When the matching checkbox is not in the DOM — because the
 * facet group is collapsed behind "show more", or the value is not rendered in the current
 * result set — the pill navigates by URL instead of doing nothing.
 */

class CollectionFilterPills extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#handleClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
  }

  /** @param {Event} event */
  #handleClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const pill = target.closest('.collection-filter-pills__pill');
    const clearBtn = target.closest('.collection-filter-pills__clear');

    if (pill) {
      this.#togglePillFilter(/** @type {HTMLElement} */ (pill));
    } else if (clearBtn) {
      this.#clearAllPills();
    }
  };

  /** @returns {Element} */
  #getSectionContainer() {
    return this.closest('.shopify-section') || document.documentElement;
  }

  #getFacetsForm() {
    const container = this.#getSectionContainer();
    return container.querySelector('facets-form-component');
  }

  /**
   * Finds every rendered checkbox that represents a given filter value.
   * @param {string} param
   * @param {string} value
   * @returns {HTMLInputElement[]}
   */
  #findCheckboxes(param, value) {
    const container = this.#getSectionContainer();

    return /** @type {HTMLInputElement[]} */ ([
      ...container.querySelectorAll(
        `input[type="checkbox"][name="${CSS.escape(param)}"][value="${CSS.escape(value)}"]`
      ),
    ]);
  }

  /**
   * Reflects the new state immediately so a click always produces visible feedback while the
   * section render is in flight. The morph replaces this with server state when it lands.
   * @param {HTMLElement} pill
   * @param {boolean} active
   */
  #setPendingState(pill, active) {
    pill.classList.toggle('collection-filter-pills__pill--active', active);
    pill.setAttribute('aria-pressed', String(active));
  }

  /** @param {HTMLElement} pill */
  #togglePillFilter(pill) {
    const param = pill.dataset.filterParam;
    const value = pill.dataset.filterValue;
    if (!param || !value) return;

    const facetsForm = this.#getFacetsForm();
    if (!facetsForm || !('updateFilters' in facetsForm)) return;

    const shouldActivate = !pill.classList.contains('collection-filter-pills__pill--active');
    const checkboxes = this.#findCheckboxes(param, value);

    this.#setPendingState(pill, shouldActivate);

    if (checkboxes.length > 0) {
      for (const checkbox of checkboxes) checkbox.checked = shouldActivate;
      /** @type {any} */ (facetsForm).updateFilters();
      return;
    }

    // No checkbox rendered for this value — fall back to the URL Shopify built for it.
    const url = pill.dataset.filterUrl;
    if (url) /** @type {any} */ (facetsForm).updateFiltersByURL(url);
  }

  #clearAllPills() {
    const facetsForm = this.#getFacetsForm();
    if (!facetsForm || !('updateFilters' in facetsForm)) return;

    const activePills = /** @type {HTMLElement[]} */ ([
      ...this.querySelectorAll('.collection-filter-pills__pill--active'),
    ]);

    const url = new URL(window.location.href);

    for (const pill of activePills) {
      const param = pill.dataset.filterParam;
      const value = pill.dataset.filterValue;
      if (!param || !value) continue;

      url.searchParams.delete(param, value);

      // Filters spanning more than one parameter (price uses .gte and .lte) name them here, since
      // deleting the pill's own param/value pair would leave the filter applied.
      for (const extra of (pill.dataset.filterClearParams || '').split(' ')) {
        if (extra) url.searchParams.delete(extra);
      }

      // Unchecked optimistically so the sidebar does not look stale while the re-render is in flight.
      for (const checkbox of this.#findCheckboxes(param, value)) checkbox.checked = false;

      this.#setPendingState(pill, false);
    }

    // Always resolve by URL rather than by form state. A pill with no checkbox (price, or a value hidden
    // behind "show more") would otherwise survive a submit built from the form.
    /** @type {any} */ (facetsForm).updateFiltersByURL(url.toString());
  }
}

if (!customElements.get('collection-filter-pills')) {
  customElements.define('collection-filter-pills', CollectionFilterPills);
}
